<template>
	<div class="similar-titles">
		<div class="detail-infos__subheading">
			<h2 class="detail-infos__subheading--label"><slot /></h2>
		</div>

		<HorizontalTitleList
			ref="horizontalTitleList"
			clickSource="similartitles"
			:additionalContexts="additionalContexts"
			:sponsoredAd="sponsoredAd"
			:emitOnScroll="true"
			:hasWatchNow="false"
			:resetScrollOnChange="true"
			:showQuickActions="true"
			:titleObjectId="title.objectId"
			:titleObjectType="title.objectType"
			:moduleGroup="moduleGroup"
			:titles="titles"
			loading="lazy"
			@scrollbarClicked="trackScroll"
			@onPosterClick="trackSimilarTitlesClicked"
			@onSponsoredRecommendationClick="trackSimilarTitlesClicked"
		>
			<template #sponsoredRecommendation>
				<slot name="sponsoredRecommendation"></slot>
			</template>
		</HorizontalTitleList>
	</div>
</template>

<script setup lang="ts">
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import type { TitleDetailSeason, TitleDetail } from '@/interfaces/title-details-graphql';
import type { SimilarTitleFragment } from '@/components/graphql/queries/GetSimilarTitles.query';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';

import HorizontalTitleList from '@/components/timeline/HorizontalTitleList.vue';

import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ImpressionTrackingEvents } from '@/enums/events';

import { computed, watch, onMounted } from 'vue';

const props = withDefaults(
	defineProps<{
		titles: SimilarTitleFragment[];
		title: NonNullable<TitleDetail | TitleDetailSeason['show']>;
		sponsoredAd?: SponsoredAdFragment | undefined;
		additionalContexts?: SnowplowContext[];
		impressionTrackingLabel?: string;
		// @todo problems with enum types - ImpressionTrackingEvents | '';
		impressionTrackingProperty?: string;
	}>(),
	{
		sponsoredAd: undefined,
		additionalContexts: () => [],
		impressionTrackingLabel: '',
		impressionTrackingProperty: '',
	}
);

watch(
	() => props.title.id,
	(newTitleId, oldTitleId) => {
		if (import.meta.client && newTitleId !== oldTitleId) {
			trackSimilarTitlesLoaded();
		}
	}
);

const moduleGroup = computed(() => [props.impressionTrackingLabel, props.impressionTrackingProperty].join('-'));

onMounted(() => {
	if (props.titles?.length) trackSimilarTitlesLoaded();
});

function trackSimilarTitlesLoaded() {
	TrackingHelper.trackEvent(
		ImpressionTrackingEvents.SIMILAR_TITLES,
		{
			action: 'loaded',
			nonInteraction: true,
			label: props.impressionTrackingLabel ?? undefined,
		},
		props.additionalContexts
	);
}

function trackSimilarTitlesClicked({ index, title }: { index: number; title: SimilarTitleFragment }) {
	TrackingHelper.trackEvent('userinteraction', {
		action: 'title_clicked',
		label: `${props.title.id},${title.id ?? ''}`,
		value: index,
		property: props.impressionTrackingProperty ?? ImpressionTrackingEvents.SIMILAR_TITLES,
	});
}

function trackScroll(label: string) {
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			label,
			action: 'horizontal_scroller_clicked',
			property: props.impressionTrackingLabel ?? undefined,
		},
		props.additionalContexts
	);
}
</script>

<style lang="scss" scoped>
.similar-titles {
	margin-bottom: 20px;
}
</style>
